import React, { useState, useEffect } from 'react';
import { cn } from '../../../utils/cn';

interface SliderInputProps {
  value: number;
  onChange: (value: number) => void;
  label?: string;
  min?: number;
  max?: number;
  step?: number;
  disabled?: boolean;
  className?: string;
}

export const SliderInput: React.FC<SliderInputProps> = ({
  value,
  onChange,
  label,
  min = 0,
  max = 1,
  step = 0.01,
  disabled = false,
  className,
}) => {
  const [localValue, setLocalValue] = useState(String(value));
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    if (!isEditing) {
      setLocalValue(String(value));
    }
  }, [value, isEditing]);

  const handleSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = parseFloat(e.target.value);
    onChange(newValue);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLocalValue(e.target.value);
  };

  const handleInputBlur = () => {
    setIsEditing(false);
    const parsed = parseFloat(localValue);
    if (!isNaN(parsed)) {
      const clamped = Math.max(min, Math.min(max, parsed));
      onChange(clamped);
      setLocalValue(String(clamped));
    } else {
      setLocalValue(String(value));
    }
  };

  const handleInputFocus = () => {
    setIsEditing(true);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      (e.target as HTMLInputElement).blur();
    } else if (e.key === 'Escape') {
      setLocalValue(String(value));
      setIsEditing(false);
      (e.target as HTMLInputElement).blur();
    }
  };

  // Calculate the fill percentage for the slider track
  const fillPercent = ((value - min) / (max - min)) * 100;

  return (
    <div className={cn('flex flex-col gap-1', className)}>
      {label && (
        <label className="text-xs text-zinc-400 select-none">{label}</label>
      )}
      <div className="flex items-center gap-2">
        <div className="flex-1 relative">
          <input
            type="range"
            min={min}
            max={max}
            step={step}
            value={value}
            onChange={handleSliderChange}
            disabled={disabled}
            className={cn(
              'w-full h-1 rounded-full appearance-none cursor-pointer',
              'bg-zinc-700',
              '[&::-webkit-slider-thumb]:appearance-none',
              '[&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:h-3',
              '[&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-blue-500',
              '[&::-webkit-slider-thumb]:cursor-pointer',
              '[&::-webkit-slider-thumb]:hover:bg-blue-400',
              '[&::-moz-range-thumb]:w-3 [&::-moz-range-thumb]:h-3',
              '[&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-blue-500',
              '[&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:cursor-pointer',
              'disabled:opacity-50 disabled:cursor-not-allowed'
            )}
            style={{
              background: `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${fillPercent}%, #3f3f46 ${fillPercent}%, #3f3f46 100%)`,
            }}
          />
        </div>
        <input
          type="text"
          value={localValue}
          onChange={handleInputChange}
          onBlur={handleInputBlur}
          onFocus={handleInputFocus}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          className={cn(
            'w-14 px-2 py-1 text-xs bg-zinc-800 border border-zinc-700 rounded',
            'text-zinc-100 text-center focus:outline-none focus:border-blue-500',
            'disabled:opacity-50 disabled:cursor-not-allowed',
            '[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none'
          )}
        />
      </div>
    </div>
  );
};
